import React, { useState } from 'react';
import { Card, Form, Input, Button, Upload, Avatar, Typography, Tabs, Select, message, Divider } from 'antd';
import { UserOutlined, UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { updateProfileApi } from '../../api/auth';
import { setUser, logout } from '../../store/slices/authSlice';
import { setLanguage } from '../../store/slices/uiSlice';
import { USER_IMAGE, SUPPORTED_LANGS } from '../../utils/constants';

const { Title } = Typography;

function ProfileTab() {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [preview, setPreview] = useState(null);

  React.useEffect(() => {
    form.setFieldsValue({ name: user?.name, state: user?.state, city: user?.city });
  }, [user, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(values).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (photoFile) fd.append('photo', photoFile);
      const res = await updateProfileApi(fd);
      dispatch(setUser(res.data?.user || { ...user, ...values }));
      message.success('Profile updated!');
    } catch {
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} size="large" style={{ maxWidth: 480 }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <Avatar
          size={80}
          src={preview || (user?.photo_url ? USER_IMAGE(user.photo_url) : null)}
          icon={<UserOutlined />}
          style={{ background: '#1a6b2a', marginBottom: 8 }}
        />
        <br />
        <Upload
          beforeUpload={(file) => {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setPreview(e.target.result);
            reader.readAsDataURL(file);
            return false;
          }}
          showUploadList={false}
          accept="image/*"
        >
          <Button icon={<UploadOutlined />} size="small">{t('upload_photo')}</Button>
        </Upload>
      </div>
      <Form.Item label={t('name')} name="name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Form.Item label={t('state')} name="state">
          <Input />
        </Form.Item>
        <Form.Item label={t('city')} name="city">
          <Input />
        </Form.Item>
      </div>
      <Button type="primary" htmlType="submit" loading={loading}
        style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>{t('save')}</Button>
    </Form>
  );
}

function LanguageTab() {
  const dispatch = useDispatch();
  const { language } = useSelector((s) => s.ui);
  const { i18n } = useTranslation();

  const handleChange = (val) => {
    dispatch(setLanguage(val));
    i18n.changeLanguage(val);
    message.success('Language updated');
  };

  return (
    <div style={{ maxWidth: 300 }}>
      <Form layout="vertical" size="large">
        <Form.Item label="App Language">
          <Select value={language} onChange={handleChange}
            options={SUPPORTED_LANGS.map((l) => ({ value: l.code, label: l.label }))} />
        </Form.Item>
      </Form>
    </div>
  );
}


export default function Settings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const items = [
    { key: 'profile', label: `👤 ${t('profile')}`, children: <ProfileTab /> },
    { key: 'language', label: `🌐 ${t('language')}`, children: <LanguageTab /> },
  ];

  return (
    <div>
      <Title level={4} style={{ color: '#1a6b2a', marginBottom: 20 }}>⚙️ {t('settings')}</Title>
      <Card style={{ borderRadius: 10, maxWidth: 600 }}>
        <Tabs items={items} />
        <Divider />
        <Button danger onClick={handleLogout} size="large">{t('logout')}</Button>
      </Card>
    </div>
  );
}
